import { useEffect } from 'react'

const CSS_VAR = '--devtools-offset'
const PANEL_SELECTOR = '[style*="--tsd-main-panel-height"]'

// Side-effect-only sibling of <TanStackDevtools>: keeps the page content shifted
// when the devtools panel is open by writing --devtools-offset on <html>. Must
// NOT wrap <TanStackDevtools> — the @tanstack/devtools-vite plugin only strips
// devtools panel imports from production builds when it can see the literal
// <TanStackDevtools> JSX in source. Hiding it behind a wrapper leaks the Solid
// devtools-ui shell into the Nitro SSR bundle and crashes every request.
export function DevtoolsLayoutOffset() {
  useEffect(() => {
    let panel: HTMLElement | null = null
    let panelObserver: MutationObserver | null = null
    let panelResize: ResizeObserver | null = null

    const setOffset = (px: number) => {
      document.documentElement.style.setProperty(CSS_VAR, `${px}px`)
    }

    const update = () => {
      if (!panel || !document.contains(panel)) {
        setOffset(0)
        return
      }
      const isOpen = getComputedStyle(panel).pointerEvents !== 'none'
      setOffset(isOpen ? Math.round(panel.getBoundingClientRect().height) : 0)
    }

    const detachPanel = () => {
      panelObserver?.disconnect()
      panelObserver = null
      panelResize?.disconnect()
      panelResize = null
      panel = null
    }

    const attachPanel = () => {
      if (panel && document.contains(panel)) return
      detachPanel()
      const found = document.querySelector<HTMLElement>(PANEL_SELECTOR)
      if (!found) return
      panel = found
      panelResize = new ResizeObserver(update)
      panelResize.observe(panel)
      panelObserver = new MutationObserver(update)
      panelObserver.observe(panel, { attributes: true, attributeFilter: ['style', 'class'] })
    }

    const bodyObserver = new MutationObserver(() => {
      attachPanel()
      update()
    })
    bodyObserver.observe(document.body, { childList: true })

    attachPanel()
    update()

    return () => {
      bodyObserver.disconnect()
      detachPanel()
      document.documentElement.style.removeProperty(CSS_VAR)
    }
  }, [])

  return null
}
